<template>
  <calendar
    :class="{
      calendar: true,
      'job-calendar': true,
      'job-calendar--editable': editable,
    }"
    ref="cal"
    :events="events"
    :selected-date="daySelected"
    :selected-event="dateSelected"
    :split-days="splitDays"
    :editable="editable"
    v-on="$listeners"
    @date-clicked="dateClicked"
    @event-clicked="eventClicked"
    @drag-create-ended="dateClicked"
    @view-change="viewChanged"
    @created="calCreated = true"
  >
  </calendar>
</template>

<script>
import LoadsData from "../Mixins/LoadsData";
import { cloneObject, toDate } from "../../helpers/common";

export default {
  mixins: [LoadsData],
  props: {
    job: {
      type: Object,
      default: null,
    },
    users: {
      default: null,
    },
    selectedDate: {
      type: Object | Number,
    },
    selectedDay: {
      type: Date | String,
    },
    typeIcons: {
      type: Object,
      default: null,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    dataUrl: {
      type: String,
      default: "test",
    },
    dataSrc: {
      type: Array,
      default() {
        return [];
      },
    },
    idKey: {
      type: String,
      default: "id",
    },
    splitDaysLabels: {
      type: Object,
      default() {
        return {
          "-4": "Zugesagt",
          "-3": "Offen",
          "-2": "Abgelehnt",
          "-1": "Angefragt",
        };
      },
    },
    splitDaysClasses: {
      default() {
        return {
          "-4": "accepted",
          "-3": "open",
          "-2": "rejected",
          "-1": "requested",
        };
      },
    },
    splitByTechnician: {
      type: Boolean,
      default: true,
    },
    preloadMonthsPrevious: {
      type: Number,
      default: 1,
    },
    preloadMonthsNext: {
      type: Number,
      default: 1,
    },
    groupLoadKey: {
      type: String,
      default: "months",
    },
    groupBy: {
      type: String | Function,
      default: function () {
        return (item) => {
          let date = new Date(item.start);
          return date.getFullYear() + "-" + (date.getMonth() + 1);
        };
      },
    },
  },
  data() {
    return {
      dateSelected: this.selectedDate,
      currentView: null,
      currentViewType: "week",
      currentViewEvents: [],
      calCreated: false,
      select_user: 0,
      users_option: null,
    };
  },
  watch: {
    selectedDate: function (newData) {
      this.dateSelected = newData;
    },
    calCreated: function (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$forceUpdate();
      }
    },
  },
  computed: {
    events() {
      if (!this.calCreated) {
        return [];
      }

      let jobDates = this.data.slice(0);
      const user_id = this.select_user;
      if (user_id > 0) {
        jobDates = jobDates.filter((x) => x.user_id == user_id);
      }
      let events = [];

      if (this.job) {
        for (const jobDate of this.job.dates) {
          const index = jobDates.findIndex((d) => d.id == jobDate.id);

          if (index != -1) {
            jobDates.splice(index, 1, Object.assign(jobDates[index], jobDate));
          } else {
            if (user_id > 0) {
              if (jobDate.user_id == user_id) {
                jobDates.push(jobDate);
              }
            } else {
              jobDates.push(jobDate);
            }
          }
        }
      }

      for (var jobDate of jobDates) {
        events.push(
          this.createEventData(
            jobDate.job ? jobDate.job : this.job,
            jobDate.type,
            jobDate
          )
        );
      }

      if (this.currentView) {
        this.currentViewEvents = events.filter(
          (e) => this.currentView.events.findIndex((ce) => ce.id == e.id) != -1
        );
      }
      return events.concat(this.additionalEvents);
    },
    additionalEvents() {
      const additionalEvents = [];

      if (this.currentViewType == "day") {
        this.currentViewEvents.forEach((eventData) => {
          if (
            this.splitByTechnician &&
            eventData.split < 0 &&
            eventData.split > -3
          ) {
            // open requests or rejected
            eventData.technician_requests.forEach((request) => {
              const newAdditionalEvent = cloneObject(eventData);

              delete newAdditionalEvent._eid;
              newAdditionalEvent.split = request.user_id;
              newAdditionalEvent.background = true;
              newAdditionalEvent.classes = [
                "job-event",
                "job-event-request",
                `job-event-request--${request.status}`,
              ];

              additionalEvents.push(newAdditionalEvent);
            });
          }
        });
      }

      return additionalEvents;
    },
    daySelected() {
      const now = new Date();

      if (this.selectedDay === null) {
        return undefined;
      } else if (this.selectedDay !== undefined) {
        return this.selectedDay;
      } else if (this.job) {
        let plannedPeriod = this.job.period_planned;
        let year =
          "y" in plannedPeriod ? plannedPeriod.y[0] : now.getFullYear();
        let plannedDate = new Date(year, 1, 1);
        let week_year = year - 2020 > 0 ? 0 : 1;
        if ("w" in plannedPeriod) {
          plannedDate = new Date(
            year,
            0,
            1 + (plannedPeriod.w[0] - week_year) * 7
          );
        } else if ("q" in plannedPeriod) {
          plannedDate = new Date(year, (plannedPeriod.q[0] - 1) * 3, 1);
        }

        return this.job.dates.length == 0 && plannedDate < now
          ? now
          : plannedDate;
      } else {
        return now;
      }
    },
    viewDate() {
      if (this.currentView) {
        return this.currentView.startDate;
      } else {
        return this.daySelected;
      }
    },
    splitDays() {
      if (this.currentViewType == "day") {
        const splitDays = [];
        const splitDaysTechnicians = [];

        this.currentViewEvents.forEach((eventData) => {
          if (
            this.splitByTechnician &&
            "technician_requests" in eventData &&
            eventData.technician_requests.length
          ) {
            for (const request of eventData.technician_requests) {
              if (!splitDaysTechnicians.some((p) => p.id == request.user_id)) {
                let technicianLabel = request.technician.name;

                if (request.technician.profile) {
                  technicianLabel =
                    '<img src="' +
                    (request.technician.profile.picture
                      ? request.technician.profile.picture
                      : "/img/user/profile-picture.svg") +
                    '" width="16" />';
                  technicianLabel += ` <span>${request.technician.profile.firstname} ${request.technician.profile.lastname}</span>`;
                }

                splitDaysTechnicians.push({
                  id: request.user_id,
                  label: technicianLabel,
                  class: "vuecal__cell-split--request-technician",
                });
              }
            }
          }

          if (
            eventData.split < 0 &&
            !splitDays.some((p) => p.id == eventData.split)
          ) {
            const splitDayData = {
              id: eventData.split,
              label: this.splitDaysLabels[eventData.split],
              class: "vuecal__cell-split--request-type",
            };

            if (eventData.split in this.splitDaysClasses) {
              splitDayData.class +=
                " " + this.splitDaysClasses[eventData.split];
            }

            splitDays.push(splitDayData);
          }
        });

        splitDays.sort((a, b) => {
          if (a.id > b.id) return 1;
          if (a.id < b.id) return -1;

          return 0;
        });

        return splitDays.concat(splitDaysTechnicians);
      }

      return [];
    },
  },
  methods: {
    addDate(date) {
      if (this.job) {
        this.job.dates.push(date);
      } else {
        this.data.push(date);
      }

      this.dateSelected = date;
    },
    removeDate(date) {
      this.removeOrReplaceDate(date);
    },
    changeDate(date, newData = undefined) {
      if (newData === undefined) {
        newData = date;
      }

      this.removeOrReplaceDate(date, newData);
    },
    removeOrReplaceDate(date, replacement = undefined) {
      this.findAndRemoveOrReplace(date, replacement);

      if (this.job) {
        this.findAndRemoveOrReplace(date, replacement, this.job.dates);
      }
    },
    dateClicked(e) {
      this.$emit("date-clicked", e);
    },
    eventClicked(e) {
      this.dateSelected = e.data;
      this.$emit("event-clicked", e);
    },
    viewChanged(e) {
      Object.entries({ Before: -1, Current: 0, After: 1 }).forEach(
        ([key, val]) => {
          e["month" + key] = new Date(
            e.startDate.getFullYear(),
            e.startDate.getMonth() + val,
            1
          );
        }
      );

      if (e.view != "years" && e.view != "year") {
        let viewMonth = new Date(
          this.viewDate.getFullYear(),
          this.viewDate.getMonth(),
          1
        );
        let newViewMonth = new Date(
          e.startDate.getFullYear(),
          e.startDate.getMonth(),
          1
        );

        if (viewMonth.getTime() != newViewMonth.getTime()) {
          let viewChangeData = {
            previous: viewMonth,
            current: newViewMonth,
            viewChangeData: e,
          };

          const groupsToLoad = [
            newViewMonth.getFullYear() + "-" + (newViewMonth.getMonth() + 1),
          ];
          const preload = {
            unshift: this.preloadMonthsPrevious,
            push: this.preloadMonthsNext,
          };

          Object.keys(preload).forEach((method) => {
            const preloadAmount = preload[method];

            for (let i = 1; i <= preloadAmount; i++) {
              const add = method == "unshift" ? i * -1 : i;
              const preloadMonth = new Date(
                newViewMonth.getFullYear(),
                newViewMonth.getMonth() + add,
                1
              );

              groupsToLoad[method](
                preloadMonth.getFullYear() + "-" + (preloadMonth.getMonth() + 1)
              );
            }
          });

          viewChangeData.dataLoaded = this.loadGroups(groupsToLoad);

          this.$emit("month-changed", viewChangeData);
        }
      }

      this.currentView = e;

      if (this.currentViewType != this.currentView.view) {
        this.currentViewType = this.currentView.view;
      }

      this.currentViewEvents = this.currentView.events;

      this.$emit("view-change", e);
    },
    createEventData(job, type, eventData = {}) {
      eventData = Object.assign({}, eventData);
      if (job) {
        eventData.job = job;
        eventData.title = `<strong>${job.id_extern}${
          job.manager
            ? job.manager.id_extern
              ? " / " + job.manager.id_extern
              : ""
            : ""
        }</strong>`;
        eventData.contentComponent = this.createEventContentComponent(
          job,
          type,
          eventData
        );
        eventData.classes = [
          "job-event",
          "job-event--type-" + type,
          "job-event--status-" + job.status,
        ];

        if (this.job && job.id != this.job.id) {
          eventData.classes.push("job-event--slight");
        }

        if (
          "technician_requests" in eventData &&
          eventData.technician_requests.length
        ) {
          eventData.classes.push("job-event--has-requests");

          const acceptedRequest = eventData.technician_requests.find(
            (p) => p.status == 2
          );

          if (acceptedRequest) {
            eventData.classes.push("job-event--accepted");

            eventData.split = this.splitByTechnician
              ? acceptedRequest.user_id
              : -4;
          } else if (
            eventData.technician_requests.every((p) => p.status == 3)
          ) {
            eventData.classes.push("job-event--rejected");
            // every request got rejected
            eventData.split = -2;
          } else {
            // there are open requests
            eventData.split = -1;
          }
        } else {
          // no technician requests yet
          eventData.split = -3;
        }
      } else {
        eventData.title = `<strong>${
          eventData.reason ? eventData.reason.reason : ""
        }</strong>`;
        eventData.contentComponent = this.createEventBreakContentComponent(
          eventData
        );
        eventData.classes = [
          "job-event",
          "job-event--type-break",
          "job-event--status-break",
        ];
        eventData.split = -4;
      }

      return eventData;
    },
    createEventBreakContentComponent(eventData = {}, type = 4) {
      const contentElements = [];

      contentElements.push(
        this.$createElement(
          "div",
          { class: `job-event__icon job-event__icon--break` },
          [
            this.$createElement("img", {
              attrs: {
                src: this.typeIcons["break"]
                  ? this.typeIcons["break"]
                  : "/img/icons/dash-circle.svg",
              },
            }),
          ]
        )
      );
      let contentSlotName = "event-content-" + "break";

      if (!(contentSlotName in this.$scopedSlots)) {
        contentSlotName =
          "event-content" in this.$scopedSlots ? "event-content" : null;
        contentElements.push(
          this.$createElement(
            "div",
            { class: "job-event__content break-content" },
            [
              // this.$scopedSlots[contentSlotName]({ job: job, date: eventData }),
              this.$createElement(
                "button",
                {
                  class:
                    "btn btn-discreet btn-small btn-icon-right btn-auto-width btn-full-width mb-xxs",
                },
                [eventData.technician.name]
              ),
            ]
          )
        );
      } else {
        if (contentSlotName) {
          contentElements.push(
            this.$createElement(
              "div",
              { class: "job-event__content break-content" },
              [this.$scopedSlots[contentSlotName]({ date: eventData })]
            )
          );
        }
      }

      return {
        render: (h) => h("div", contentElements),
      };
    },

    createEventContentComponent(job, type = null, eventData = {}) {
      const contentElements = [];

      job.locations.forEach((location) => {
        contentElements.push(
          this.$createElement("p", [
            location.street +
              " " +
              location.street_number +
              ", " +
              location.postal_code +
              " " +
              location.city,
          ])
        );
      });

      if (type && this.typeIcons && type in this.typeIcons) {
        contentElements.push(
          this.$createElement(
            "div",
            { class: `job-event__icon job-event__icon--${type}` },
            [
              this.$createElement("img", {
                attrs: { src: this.typeIcons[type] },
              }),
            ]
          )
        );
      }

      let contentSlotName = "event-content-" + job.status;

      if (!(contentSlotName in this.$scopedSlots)) {
        contentSlotName =
          "event-content" in this.$scopedSlots ? "event-content" : null;
      }

      if (contentSlotName) {
        contentElements.push(
          this.$createElement("div", { class: "job-event__content" }, [
            this.$scopedSlots[contentSlotName]({ job: job, date: eventData }),
          ])
        );
      }

      return {
        render: (h) => h("div", contentElements),
      };
    },
  },
};
</script>